// src/services/elasticsearchService.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API || 'https://api.vidal-app.com';

const elasticsearchApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token e headers
elasticsearchApi.interceptors.request.use(
  (config) => {
    // Modo demo: simula mutações (archive/restore/delete), mantém search/count
    const isDemo = (() => { try { return localStorage.getItem('vidal_demo_mode') === 'true'; } catch { return false; } })();
    const method = (config.method || 'get').toLowerCase();
    const isReadPost = /(search|count|stats|kpis|aggregat|export|relatorio|report)/i.test(config.url || '');
    if (isDemo && !['get', 'head'].includes(method) && !isReadPost) {
      config.adapter = () => Promise.resolve({
        data: { success: true, simulated: true, demo: true },
        status: 200, statusText: 'OK (demo)', headers: {}, config,
      });
    }

    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    const userId = authData.userId;
    const projeto = localStorage.getItem('vidal_selected_project');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (userId) {
      config.headers.user = userId;
    }

    if (projeto && !config.headers.projeto) {
      config.headers.projeto = projeto;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para atualizar token
elasticsearchApi.interceptors.response.use(
  (response) => {
    const newToken = response.headers.authorization;
    if (newToken) {
      const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
      const token = newToken.replace('Bearer ', '');
      localStorage.setItem('vidal_auth', JSON.stringify({ ...authData, token }));
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('vidal_auth');
      localStorage.removeItem('vidal_user');
      localStorage.removeItem('vidal_selected_project');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

class ElasticsearchService {

  /**
   * Lista todos os índices
   */
  async listIndices() {
    try {
      const response = await elasticsearchApi.get('/elasticsearch/indices');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar índices:', error);
      throw error;
    }
  }

  /**
   * Obtém detalhes de um índice específico
   */
  async getIndexDetails(indexName) {
    try {
      const response = await elasticsearchApi.get(`/elasticsearch/indices/${indexName}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter detalhes do índice ${indexName}:`, error);
      throw error;
    }
  }

  /**
   * Obtém um documento de exemplo de um índice
   */
  async getSampleDocument(indexName) {
    try {
      const response = await elasticsearchApi.get(`/elasticsearch/indices/${indexName}/sample`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter documento de exemplo do índice ${indexName}:`, error);
      throw error;
    }
  }

  /**
   * Busca documentos em um índice
   */
  async searchDocuments(indexName, query = '*', from = 0, size = 10, fields = []) {
    try {
      const response = await elasticsearchApi.post(`/elasticsearch/indices/${indexName}/search`, {
        query,
        from,
        size,
        fields
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar documentos no índice ${indexName}:`, error);
      throw error;
    }
  }

  /**
   * Conta documentos em um índice
   */
  async countDocuments(indexName, query = null) {
    try {
      const response = await elasticsearchApi.post(`/elasticsearch/indices/${indexName}/count`, {
        query
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao contar documentos no índice ${indexName}:`, error);
      throw error;
    }
  }

  /**
   * Obtém um documento específico por ID
   */
  async getDocument(indexName, docId) {
    try {
      const response = await elasticsearchApi.get(`/elasticsearch/indices/${indexName}/documents/${docId}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter documento ${docId}:`, error);
      throw error;
    }
  }

  /**
   * Obtém health do cluster
   */
  async getClusterHealth() {
    try {
      const response = await elasticsearchApi.get('/elasticsearch/cluster/health');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter health do cluster:', error);
      throw error;
    }
  }

  /**
   * Obtém estatísticas do cluster
   */
  async getClusterStats() {
    try {
      const response = await elasticsearchApi.get('/elasticsearch/cluster/stats');
      return response.data;
    } catch (error) {
      console.error('Erro ao obter stats do cluster:', error);
      throw error;
    }
  }

  /**
   * Arquiva um índice (cria backup)
   */
  async archiveIndex(indexName, deleteAfter = false) {
    try {
      const response = await elasticsearchApi.post(`/elasticsearch/indices/${indexName}/archive`, {
        deleteAfter
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao arquivar índice ${indexName}:`, error);
      throw error;
    }
  }

  /**
   * Lista todos os backups disponíveis
   */
  async listBackups() {
    try {
      const response = await elasticsearchApi.get('/elasticsearch/backups');
      return response.data;
    } catch (error) {
      console.error('Erro ao listar backups:', error);
      throw error;
    }
  }

  /**
   * Restaura um backup
   */
  async restoreBackup(backupId, newIndexName = null) {
    try {
      const response = await elasticsearchApi.post(`/elasticsearch/backups/${backupId}/restore`, {
        newIndexName
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao restaurar backup ${backupId}:`, error);
      throw error;
    }
  }

  /**
   * Deleta um backup
   */
  async deleteBackup(backupId) {
    try {
      const response = await elasticsearchApi.delete(`/elasticsearch/backups/${backupId}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao deletar backup ${backupId}:`, error);
      throw error;
    }
  }
}

export default new ElasticsearchService();
